import React, { useEffect, useMemo, useState } from "react";
import { Button, Card, Descriptions, Divider, Drawer, Input, Select, Table, Tag, Timeline } from "antd";
import { FileExcelOutlined, SearchOutlined } from "@ant-design/icons";
import utils from "utils";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { find } from "lodash";
import {
  FIRESTORE_LOYALTY_CLAIMS_TABLE,
  FIRESTORE_LOYALTY_REWARDS_TABLE,
  FIRESTORE_USER_ANALYTICS_TABLE,
} from "../../../../constants/FirestoreConstant";
import { useFirestoreConnect } from "react-redux-firebase";
import { isLandelijk } from "../../../../functions/region";
import ExcelExport from "../../../../components/shared-components/ExcelExport";
import { getCustomerExcelExportData } from "../../../../functions/excel";
import { DATE_FORMAT_DD_MM_YYYY } from "../../../../constants/DateConstant";
import { COMPANY_CONSTANTS } from "../../../../constants/CompanyConstants";
import dayjs from "dayjs";

const Customers = () => {
  const webshopUsers = useSelector((state) => state.firestoreReducer.ordered.webshopUsers);
  const loyaltyUsers = useSelector((state) => state.firestoreReducer.ordered.loyaltyUsers);
  const orders = useSelector((state) => state.firestoreReducer.ordered.orders);

  const [filteredCustomerList, setFilteredCustomerList] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [regionFilter, setRegionFilter] = useState("all");
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const { t } = useTranslation();

  // Connect to Firestore to get all RFM analyses, loyalty claims and rewards
  useFirestoreConnect([
    { collection: FIRESTORE_USER_ANALYTICS_TABLE },
    { collection: FIRESTORE_LOYALTY_CLAIMS_TABLE },
    { collection: FIRESTORE_LOYALTY_REWARDS_TABLE },
  ]);

  const userAnalytics = useSelector((state) => state.firestoreReducer.ordered.userAnalytics);
  const loyaltyClaims = useSelector((state) => state.firestoreReducer.ordered.loyaltyClaims);
  const loyaltyRewards = useSelector((state) => state.firestoreReducer.ordered.loyaltyRewards);

  const filterByRegion = (list, region) => {
    if (!list || region === "all") return list;
    return list.filter((customer) => {
      const zip = customer?.address?.delivery?.zipCode;
      const landelijk = isLandelijk(zip);
      return region === "landelijk" ? landelijk : !landelijk;
    });
  };

  const customerList = useMemo(() => {
    if (!webshopUsers || !loyaltyUsers) return null;
    return webshopUsers.map((user) => {
      const loyaltyUser = find(loyaltyUsers, (u) => u.id === user.id);
      const userAnalyticsObject = userAnalytics ? find(userAnalytics, (ua) => ua.id === user.id) : null;
      const mergedUser = { ...user };
      if (loyaltyUser) {
        mergedUser.currentPoints = loyaltyUser.currentPoints;
        mergedUser.totalPointsEarned = loyaltyUser.totalPointsEarned;
        mergedUser.totalPointsSpent = loyaltyUser.totalPointsSpent;
      }
      if (userAnalyticsObject) {
        mergedUser.currentRfm = userAnalyticsObject.currentRfm;
        mergedUser.rfmHistory = userAnalyticsObject.rfmHistory;
        mergedUser.rfmMetrics = userAnalyticsObject.rfmMetrics;
      }
      return mergedUser;
    });
  }, [webshopUsers, loyaltyUsers, userAnalytics]);

  useEffect(() => {
    if (customerList) {
      setFilteredCustomerList(customerList);
    }
  }, [customerList]);

  const tableColumns = [
    {
      title: t("email"),
      dataIndex: "email",
      render: (data, record) => (
        <a
          onClick={() => {
            setSelectedCustomer(record);
            setDrawerVisible(true);
          }}
          style={{ cursor: "pointer", fontWeight: 500 }}
        >
          {data}
        </a>
      ),
      sorter: (a, b) => utils.antdTableSorter(a, b, "email"),
    },
    {
      title: t("name"),
      dataIndex: "firstName",
      render: (_, data) =>
        data?.firstName && data?.lastName ? <span>{data.firstName + " " + data.lastName}</span> : <span> - </span>,
    },
    {
      title: t("customer_segment"),
      dataIndex: ["currentRfm", "segment"],
    },
    {
      title: t("customer.loyalty_points_current"),
      dataIndex: "currentPoints",
      render: (data) => (data ? <span>{data}</span> : <span> - </span>),
    },
    {
      title: t("customer.loyalty_points_total"),
      dataIndex: "totalPointsEarned",
      render: (data) => (data ? <span>{data}</span> : <span> - </span>),
      sorter: (a, b) => utils.antdTableSorter(a, b, "totalPointsEarned"),
    },
    {
      title: t("customer.loyalty_points_spent"),
      dataIndex: "totalPointsSpent",
      render: (data) => (data ? <span>{data}</span> : <span> - </span>),
      sorter: (a, b) => utils.antdTableSorter(a, b, "totalPointsSpent"),
    },
  ];

  const onSearch = (e) => {
    const value = e.currentTarget.value;
    setSearchText(value);
    const regionFiltered = filterByRegion(customerList, regionFilter);
    if (value) {
      setFilteredCustomerList(utils.wildCardSearchOnCustomer(regionFiltered, value));
    } else {
      setFilteredCustomerList(regionFiltered);
    }
  };

  const onRegionFilterChange = (value) => {
    setRegionFilter(value);
    setSearchText("");
    setFilteredCustomerList(filterByRegion(customerList, value));
  };

  const getCustomerClaims = (userId) => {
    if (!loyaltyClaims || !userId) return [];
    return loyaltyClaims.filter((claim) => claim.userId === userId).sort((a, b) => b.dateTime - a.dateTime);
  };

  const getRewardById = (rewardId) => {
    if (!loyaltyRewards || !rewardId) return null;
    return find(loyaltyRewards, (reward) => reward.id === rewardId);
  };

  return (
    <Card>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 8,
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Input
            value={searchText}
            placeholder={t("search")}
            prefix={<SearchOutlined />}
            onChange={(e) => onSearch(e)}
            style={{ width: "fit-content" }}
          />{" "}
          <Select
            defaultValue="all"
            style={{ minWidth: 180 }}
            onChange={onRegionFilterChange}
            options={[
              { label: "Alle", value: "all" },
              { label: "Regionaal", value: "regionaal" },
              { label: "Landelijk", value: "landelijk" },
            ]}
          />
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {!!filteredCustomerList && filteredCustomerList.length > 0 && (
            <Button>
              <ExcelExport
                data={getCustomerExcelExportData(filteredCustomerList, orders)}
                fileName={
                  COMPANY_CONSTANTS.WEBSITE_NAME +
                  " klanten export " +
                  dayjs().format(DATE_FORMAT_DD_MM_YYYY) +
                  (regionFilter !== "all" ? " " + regionFilter.toUpperCase() : "")
                }
              >
                <FileExcelOutlined /> {t("export")}
              </ExcelExport>
            </Button>
          )}
        </div>
      </div>
      <div className="table-responsive">
        <Table
          showSorterTooltip={false}
          columns={tableColumns}
          dataSource={filteredCustomerList}
          rowKey="id"
          scroll={{ x: 1300 }}
        />
      </div>
      <Drawer
        title={
          selectedCustomer
            ? `${selectedCustomer.firstName || ""} ${selectedCustomer.lastName || ""}`
            : t("customer.customer")
        }
        placement="right"
        width={720}
        onClose={() => {
          setDrawerVisible(false);
          setSelectedCustomer(null);
        }}
        open={drawerVisible}
      >
        {selectedCustomer && (
          <div>
            <Descriptions title={t("customer.loyalty_info")} bordered column={1}>
              <Descriptions.Item label={t("email")}>{selectedCustomer.email}</Descriptions.Item>
              <Descriptions.Item label={t("customer.loyalty_points_current")}>
                <Tag color="blue" style={{ fontSize: 16, padding: "4px 12px" }}>
                  {selectedCustomer.currentPoints || 0} {t("points")}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t("customer.loyalty_points_total")}>
                {selectedCustomer.totalPointsEarned || 0} {t("points")}
              </Descriptions.Item>
              <Descriptions.Item label={t("customer.loyalty_points_spent")}>
                {selectedCustomer.totalPointsSpent || 0} {t("points")}
              </Descriptions.Item>
            </Descriptions>

            <Divider orientation="left">{t("customer.claimed_rewards")}</Divider>
            {getCustomerClaims(selectedCustomer.id).length > 0 ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                {getCustomerClaims(selectedCustomer.id).map((claim) => {
                  const reward = getRewardById(claim.rewardId);
                  return (
                    <Card
                      key={claim.id}
                      size="small"
                      style={{
                        borderLeft: claim.cashedIn ? "4px solid #52c41a" : "4px solid #faad14",
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ marginBottom: 8 }}>
                            <strong>{reward?.title || t("customer.unknown_reward")}</strong>
                          </div>
                          {reward?.description && (
                            <div style={{ fontSize: 12, color: "#666", marginBottom: 8 }}>{reward.description}</div>
                          )}
                          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                            <Tag color="blue">
                              {claim.points} {t("points")}
                            </Tag>
                            <Tag color={claim.cashedIn ? "green" : "orange"}>
                              {claim.cashedIn ? t("customer.claimed") : t("customer.not_claimed")}
                            </Tag>
                            {claim.rewardType && (
                              <Tag>
                                {claim.rewardType === "free_product" ? t("customer.free_product") : claim.rewardType}
                              </Tag>
                            )}
                          </div>
                        </div>
                        <div style={{ textAlign: "right", minWidth: 100 }}>
                          <div style={{ fontSize: 12, color: "#999" }}>
                            {dayjs(claim.dateTime * 1000).format(DATE_FORMAT_DD_MM_YYYY)}
                          </div>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <p>{t("customer.no_claimed_rewards")}</p>
            )}
          </div>
        )}
      </Drawer>
    </Card>
  );
};

export default Customers;
